<template>
  <div class="document-viewer">
    <b-overlay :show="isFetching" rounded="sm" spinner-variant="primary">
      <DocumentTypeGrid
        :document-types="documentTypes"
        :generating-document="generatingDocument"
        :template-documents="templateDocuments"
        :signing-template="signingTemplate"
        @generate="handleDocumentGenerate"
        @template-sign="handleTemplateSign"
        @template-upload="handleTemplateUpload"
        @template-delete="handleTemplateDelete"
      />

      <div class="documents-toolbar">
        <h2 class="documents-title">เอกสาร</h2>
        <span class="documents-count" :aria-label="`${patientDocuments.length} ไฟล์`">{{ patientDocuments.length }}</span>
        <span class="documents-spacer"></span>
        <div class="documents-search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            v-model.trim="search"
            type="search"
            class="form-control"
            placeholder="ค้นหาเอกสาร"
            aria-label="ค้นหาเอกสาร"
          />
        </div>
        <b-button variant="primary" class="documents-upload" @click="showUploadModal('Document')">
          <i class="fas fa-upload" aria-hidden="true"></i> อัปโหลดเอกสาร
        </b-button>
      </div>

      <CategoryCard
        ref="documentCard"
        title="เอกสาร"
        category="Document"
        layout="list"
        unit="ไฟล์"
        :collapsed-limit="20"
        :items="matchingDocuments"
        :selected-files="selectedFiles"
        :patient-id="patient.id"
        :file-name-of="downloadNameOf"
        @upload="showUploadModal"
        @upload-success="handleUploadSuccess"
        @upload-error="handleUploadError"
        @select="toggleFileSelection"
        @preview="handlePreview"
        @download="handleDownload"
        @delete="handleDelete"
        @sign="openSignTab"
      />
    </b-overlay>

    <GalleryActions
      :selected-files="selectedFiles"
      :select-all-label="selectAllLabel"
      :notice="downloadNotice"
      :can-share="canShareFiles"
      :share-ready="!!preparedShare"
      @select-all="selectAllIn('Document')"
      @clear="clearSelection"
      @bulk-download="handleBulkDownload"
      @bulk-delete="handleBulkDelete"
      @share="handleShare"
    />
    <MobileUploadBar
      v-if="selectedFiles.length === 0"
      pick-label="เลือกไฟล์"
      capture-label="ถ่ายเอกสาร"
      @pick="showUploadModal('Document')"
      @captured="files => $refs.uploadFiles.show({ category: 'Document', files })"
    />

    <!-- Hidden Document Generator -->
    <DocumentGenerator
      v-if="currentDocumentType !== 'medicalCert'"
      :key="currentDocumentType"
      ref="documentGenerator"
      :document-type="currentDocumentType"
      :modal-id="`${currentDocumentType}-modal`"
      :modal-ref="`${currentDocumentType}Modal`"
      modal-class="component-modal-treatment-planning"
      style="display: none"
    />

    <!-- Medical certificate edit-on-document modal -->
    <MedicalCertEditor
      v-if="currentDocumentType === 'medicalCert'"
      ref="medicalCertEditor"
      modal-id="medicalCert-modal"
      modal-ref="medicalCertModal"
    />

    <!-- Lightbox for document preview -->
    <Lightbox
      :index="lightBoxIndex"
      :items="lightBoxList"
      :toolbar="[
        'counter',
        'zoom',
        'slide',
        'rotate',
        'gallery',
        'fullscreen',
        'close',
      ]"
      @close="resetLightbox"
    />

    <!-- Modals -->
    <UploadFilesModal
      :patientId="patient.id"
      :patientDn="patient.dn"
      ref="uploadFiles"
      @uploadedFiles="handleDocumentUploads"
      @uploadedTemplateFiles="onFilesUploaded"
    />
    <Loading v-if="isLoading" />
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GalleryMixin from "@/mixins/GalleryMixin";
import ClinicFormPermissionMixin from "@/mixins/ClinicFormPermissionMixin";
import DocumentGenerator from "@/components/modal/DocumentGenerator/DocumentGenerator.vue";
import MedicalCertEditor from "@/components/modal/DocumentGenerator/MedicalCertEditor.vue";
import { documentTypes } from "@/components/modal/DocumentGenerator/config/documentConfig";
import { TEMPLATE_FETCH_PARAMS } from "@/constants";
import DocumentTypeGrid from "./DocumentTypeGrid.vue";
import CategoryCard from "../gallery/CategoryCard.vue";
import GalleryActions from "../gallery/GalleryActions.vue";
import MobileUploadBar from "../gallery/MobileUploadBar.vue";
import { pasteBelongsHere } from "@/utils/pasteTarget";
import UploadFilesModal from "@/components/modal/UploadFiles.vue";
import Dialog from "@/components/modal/Dialog.vue";
import Loading from "@/components/Loading.vue";
import VueLightbox from "vue-lightbox-lite";
import "vue-lightbox-lite/dist/index.css";

export default {
  name: "DocumentViewer",
  mixins: [GalleryMixin, ClinicFormPermissionMixin],
  components: {
    DocumentGenerator,
    MedicalCertEditor,
    DocumentTypeGrid,
    CategoryCard,
    GalleryActions,
    MobileUploadBar,
    UploadFilesModal,
    Dialog,
    Loading,
    Lightbox: VueLightbox,
  },
  props: {
    patient: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentDocumentType: "empty",
      generatingDocument: null,
      signingTemplate: null,
      search: "",
    };
  },
  computed: {
    zipLabel() {
      return "เอกสาร";
    },
    patientDocuments() {
      return this.filteredList("Document");
    },
    matchingDocuments() {
      const query = this.search.toLowerCase();
      if (!query) return this.patientDocuments;
      return this.patientDocuments.filter(f =>
        (f.remark || "").toLowerCase().includes(query)
      );
    },
    selectAllLabel() {
      const total = this.patientDocuments.length;
      return total > this.selectedFiles.length ? `เลือกทั้งหมด (${total})` : null;
    },
    ...mapGetters({
      canGenerateDocument: "moduleBranch/canGenerateDocument",
    }),
    // ไม่รวมในแพ็ก = locked (โชว์พร้อมป้าย "อัปเกรด") ไม่ใช่ disabled ที่ซ่อนทิ้ง
    documentTypes() {
      const isLocked = (item) =>
        Boolean(item.checkForSubscription) && !this.canGenerateDocument;
      return documentTypes.map(item => {
        if (item.options) {
          return {
            ...item,
            options: item.options.map(option => ({
              ...option,
              locked: isLocked(option),
            })),
          };
        }
        return { ...item, locked: isLocked(item) };
      });
    },
    templateDocuments() {
      // Get template documents from the gallery mixin, filter to PDF only
      const templates = this.filteredList("Template") || [];
      return templates.filter(template => template.fileType === 'application/pdf');
    },
  },
  methods: {
    async showDocument() {
      try {
        await this.$nextTick();

        const ref =
          this.currentDocumentType === "medicalCert"
            ? this.$refs.medicalCertEditor
            : this.$refs.documentGenerator;

        if (!ref) {
          console.warn("Document generator reference not found");
          return;
        }

        ref.show(this.patient);
      } catch (error) {
        console.error("Error showing document:", error);
      }
    },

    // Ctrl+V anywhere in this tab adds to the patient's documents; the
    // upload dialog takes it while it is open.
    onPaste(event) {
      const modal = this.$refs.uploadFiles;
      if (modal && modal.isOpen) return;
      if (!pasteBelongsHere(event.target, this.$el)) return;
      const files = Array.from((event.clipboardData && event.clipboardData.files) || []);
      if (files.length === 0 || !this.$refs.documentCard) return;
      event.preventDefault();
      this.$refs.documentCard.receiveFiles(files, "วางจากคลิปบอร์ด");
    },

    handleDocumentUploads(files) {
      this.onFilesUploaded(files);
    },

    async handleDocumentGenerate(documentType) {
      try {
        this.generatingDocument = documentType;
        this.currentDocumentType = documentType;

        await this.$nextTick();

        const ref =
          documentType === "medicalCert"
            ? this.$refs.medicalCertEditor
            : this.$refs.documentGenerator;

        if (!ref) {
          console.warn("Document generator reference not found");
          return;
        }

        ref.show(this.patient);
      } catch (error) {
        console.error("Error generating document:", error);
      } finally {
        // Reset generating state after a delay to show completion
        setTimeout(() => {
          this.generatingDocument = null;
        }, 1000);
      }
    },

    handleUploadSuccess({ files = [] }) {
      this.onFilesUploaded(files);
    },

    async handleTemplateSign(templateId) {
      try {
        this.signingTemplate = templateId;

        // Find the template document
        const template = this.templateDocuments.find(t => t.id === templateId);
        if (!template) {
          console.error("Template not found:", templateId);
          return;
        }

        // Use the existing openSignTab method (from GalleryMixin)
        this.openSignTab(template);
      } catch (error) {
        console.error("Error opening template for signing:", error);
      } finally {
        // Reset signing state after a delay
        setTimeout(() => {
          this.signingTemplate = null;
        }, 1000);
      }
    },

    handleTemplateUpload() {
      // อัปโหลดแบบฟอร์มคลินิกได้เฉพาะแพ็กที่จ่ายเงิน (ปุ่มติดล็อกอยู่แล้ว กันซ้ำตรงนี้)
      if (!this.canUploadClinicForm) return;
      this.showUploadModal("Template");
    },

    // ลบแบบฟอร์มคลินิกที่ไม่ใช้แล้ว — soft delete ลงถังขยะ (กู้คืนได้) เหมือนไฟล์อื่น
    // ไม่ต้อง refetch: deleteMediaFile ตัด id ออกจาก state.fileTemplate ให้แล้วเมื่อ 200
    async handleTemplateDelete(templateId) {
      if (!this.canManageClinicForm) return;

      const template = this.templateDocuments.find(t => t.id === templateId);
      if (!template) {
        console.error("Template not found:", templateId);
        return;
      }

      const templateName = template.name || template.remark || "แบบฟอร์มนี้";
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ลบแบบฟอร์มนี้ ?",
          `"${templateName}" จะถูกย้ายไปถังขยะ และหายจากรายการแบบฟอร์มของสาขานี้ (เอกสารที่คนไข้เซ็นไปแล้วยังอยู่ครบ)`
        );
        if (!isConfirmed) return;

        this.isLoading = true;
        const res = await this.deleteMediaFile({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          id: templateId,
        });
        if (res.status !== 200) {
          throw new Error(`Unexpected delete status ${res.status}`);
        }
      } catch (err) {
        console.error("Error deleting template:", err);
        this.$refs.Dialog.showAlert("ลบไม่สำเร็จ", "ไม่สามารถลบแบบฟอร์มนี้ได้");
      } finally {
        this.isLoading = false;
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("paste", this.onPaste);
  },
  mounted() {
    document.addEventListener("paste", this.onPaste);
    // Fetch both regular files and template files for document mode
    this.fetchMediaFiles({
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
      params: { patientId: this.patient.id, limit: this.limit },
    });

    this.fecthMediaFilesTemplate({
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
      params: { ...TEMPLATE_FETCH_PARAMS },
    });
  },
};
</script>

<style scoped>
/* พื้นเทาอ่อนเหมือนแท็บรูป ให้การ์ดลอยเด่น */
.document-viewer {
  max-width: 100%;
  padding: 1.25rem 1.25rem 5rem;
  border-radius: 18px;
  background: #f4f5f7;
  color: #101828;
}
.documents-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin: 1.75rem 0 1rem;
}
.documents-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.documents-count {
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  background: #e7eaf0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3d4557;
}
.documents-spacer {
  flex: 1 1 auto;
}
.documents-search {
  position: relative;
  width: 260px;
}
.documents-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #5d6679;
}
.documents-search .form-control {
  height: 42px;
  padding-left: 2.5rem;
  border-radius: 999px;
  border-color: #d7dbe3;
}
.documents-upload {
  height: 42px;
  padding: 0 1.125rem;
  border-radius: 999px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(11, 99, 197, 0.25);
}
@media (max-width: 575.98px) {
  .document-viewer {
    padding: 0.875rem 0.875rem 0;
    margin: 0 -0.5rem;
    border-radius: 16px;
  }
  .documents-toolbar {
    margin-top: 1.25rem;
  }
  .documents-title {
    font-size: 1.5rem;
  }
  .documents-upload {
    display: none;
  }
  .documents-search {
    order: 5;
    width: 100%;
  }
  .documents-search .form-control {
    font-size: 16px;
  }
}
</style>
